/**
 * TrajectoryOverlay - Displays trajectory indicators on the map
 *
 * Shows directional arrows on countries indicating regulatory trajectory:
 * - ↗ Opening (green) - markets liberalizing
 * - → Stable (gray) - no significant change
 * - ↘ Tightening (orange) - increasing restrictions
 * - ⊗ Closing (red) - markets restricting/banning
 */

import { Marker } from 'react-simple-maps';
import { memo, useMemo } from 'react';
import { getTrajectoryColor, type Trajectory } from '@/data/regulatoryData';
import type { CountryScore } from '@/services/scoreService';

interface TrajectoryOverlayProps {
  jurisdictions: CountryScore[];
  visible: boolean;
}

// Approximate centroid coordinates for trajectory arrow positioning
// These are offset slightly to appear in corner rather than center
const jurisdictionCoordinates: Record<string, [number, number]> = {
  // Europe
  GB: [-2, 54],
  MT: [14.5, 35.9],
  DE: [10, 51],
  NL: [5, 52],
  SE: [16, 62],
  ES: [-4, 40],
  IT: [12, 43],
  PT: [-8, 39.5],
  FR: [2, 47],
  AT: [14, 47.5],
  BE: [4.5, 50.8],
  GR: [22, 39],
  DK: [10, 56],
  IE: [-8, 53.5],
  PL: [19, 52],
  CZ: [15.5, 49.8],
  HU: [19.5, 47],
  RO: [25, 46],
  BG: [25, 43],
  HR: [16, 45.5],
  FI: [26, 64],
  NO: [10, 62],
  CH: [8, 47],

  // APAC
  AU: [134, -25],
  NZ: [174, -41],
  JP: [138, 36],
  KR: [128, 36],
  MO: [113.5, 22.2],
  SG: [104, 1.3],
  PH: [122, 12],
  IN: [79, 22],

  // LATAM
  CO: [-74, 4],
  MX: [-102, 24],
  BR: [-52, -14],
  AR: [-64, -34],
  CL: [-71, -35],
  PE: [-76, -10],
  PA: [-80, 9],
  CR: [-84, 10],

  // Africa & Middle East
  ZA: [25, -29],
  KE: [38, 0],
  NG: [8, 10],
  AE: [54, 24],

  // US States (positioned within state boundaries)
  'US-NV': [-117, 39],
  'US-NJ': [-74.5, 40],
  'US-PA': [-77.5, 41],
  'US-MI': [-85, 44],
  'US-WV': [-80.5, 39],
  'US-CT': [-73, 41.6],
  'US-IN': [-86, 40],
  'US-IA': [-93.5, 42],
  'US-CO': [-105.5, 39],
  'US-AZ': [-111.5, 34],
  'US-VA': [-79, 37.5],
  'US-MD': [-77, 39],
  'US-NY': [-75, 43],
  'US-OH': [-83, 40.5],
  'US-MA': [-71.5, 42.4],
  'US-IL': [-89.5, 40],
  'US-LA': [-92, 31],
  'US-MS': [-90, 33],
  'US-TN': [-86, 36],
  'US-KY': [-85.5, 38],
  'US-NC': [-79.5, 35.5],
  'US-FL': [-82, 28],
  'US-KS': [-98.5, 39],
  'US-NH': [-71.5, 44],
  'US-RI': [-71.5, 41.7],

  // Canadian Provinces
  'CA-ON': [-85, 50],
  'CA-BC': [-125, 54],
  'CA-AB': [-115, 55],
  'CA-QC': [-72, 52],
  'CA-MB': [-98, 55],
  'CA-SK': [-106, 55],
  'CA-NS': [-63, 45],
  'CA-NB': [-66, 46.5],
  'CA-NL': [-57, 53],
  'CA-PE': [-63, 46.3],
};

// Get arrow character and rotation based on trajectory
const getTrajectoryArrow = (trajectory: Trajectory): { char: string; rotation: number } => {
  switch (trajectory) {
    case 'opening':
      return { char: '→', rotation: -45 }; // Points up-right
    case 'stable':
      return { char: '→', rotation: 0 }; // Points right
    case 'tightening':
      return { char: '→', rotation: 45 }; // Points down-right
    case 'closing':
      return { char: '⊗', rotation: 0 }; // X symbol (no rotation)
    default:
      return { char: '→', rotation: 0 };
  }
};

function TrajectoryOverlay({ jurisdictions, visible }: TrajectoryOverlayProps) {
  // Filter jurisdictions that have coordinates and trajectory data
  const markersData = useMemo(() => {
    if (!visible) return [];

    return jurisdictions
      .filter((j) => {
        const coords = jurisdictionCoordinates[j.code];
        const trajectory = j.trajectory;
        return coords && trajectory;
      })
      .map((j) => {
        const coords = jurisdictionCoordinates[j.code]!;
        const trajectory = (j.trajectory || 'stable') as Trajectory;
        const { char, rotation } = getTrajectoryArrow(trajectory);
        const color = getTrajectoryColor(trajectory);

        return {
          code: j.code,
          coordinates: coords,
          char,
          rotation,
          color,
          trajectory,
        };
      });
  }, [jurisdictions, visible]);

  if (!visible || markersData.length === 0) {
    return null;
  }

  return (
    <>
      {markersData.map((marker) => (
        <Marker key={`trajectory-${marker.code}`} coordinates={marker.coordinates}>
          <g transform="translate(-6, -6)">
            {/* Background circle for better visibility */}
            <circle
              r={8}
              fill="rgba(0, 0, 0, 0.6)"
              stroke={marker.color}
              strokeWidth={1}
              cx={6}
              cy={6}
            />
            {/* Trajectory indicator */}
            <text
              textAnchor="middle"
              dominantBaseline="central"
              x={6}
              y={6}
              style={{
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '10px',
                fontWeight: 'bold',
                fill: marker.color,
                transform: `rotate(${marker.rotation}deg)`,
                transformOrigin: '6px 6px',
              }}
            >
              {marker.char}
            </text>
          </g>
        </Marker>
      ))}
    </>
  );
}

export default memo(TrajectoryOverlay);
